import { ProspectConversation, ProspectConversationDetail, ProspectMessage } from 'model/ProspectMessage';
import { ProspectMessageState, ProspectMessageStatus } from './prospectMessageState';
import {
    PROSPECTMESSAGES_CLEAR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_START_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATION_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_START_ACTION_TYPE,
    PROSPECTMESSAGES_FETCH_CONVERSATIONS_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_PATCH_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_RESET_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_ERROR_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_START_ACTION_TYPE,
    PROSPECTMESSAGES_SAVE_SUCCESS_ACTION_TYPE,
    PROSPECTMESSAGES_SET_STORE_START_ACTION_TYPE,
    ProspectMessagesAction,
    SAVE_NEW_MESSAGE_ERROR_ACTION_TYPE,
    SAVE_NEW_MESSAGE_START_ACTION_TYPE,
    SAVE_NEW_MESSAGE_SUCCESS_ACTION_TYPE
} from 'actions/prospectMessage/prospectMessageActionsTypes';
import moment from 'moment';

export const initialState: ProspectMessageState = {
    status: ProspectMessageStatus.Initial,
    loading: false,
    error: null,
    saveMessageError: null,
    prospectConversations: [],
    metaProspectConversations: null,
    prospectConversationDetail: null,
    filter: null,
    query: null
};

const updatedConversations = (message: ProspectMessage, state: ProspectMessageState): Array<ProspectConversation> => {
    return state.prospectConversations.map(conversation => {
        if (conversation.prospect.id === message.prospectId) {
            return {
                ...conversation,
                lastMessage: message
            };
        }
        return conversation;
    });
};

export const prospectMessageReducer = (state = initialState, action: ProspectMessagesAction): ProspectMessageState => {
    switch (action.type) {
        case PROSPECTMESSAGES_RESET_ACTION_TYPE:
            return {
                ...initialState
            };
        case PROSPECTMESSAGES_CLEAR_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.Cleared,
                loading: false,
                error: null,
                saveMessageError: null,
                prospectConversationDetail: null
            };
        case PROSPECTMESSAGES_SET_STORE_START_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.Setting,
                loading: true
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATIONS_START_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversations,
                loading: true
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATIONS_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversationsSuccess,
                loading: false,
                prospectConversations:
                    action.payload.meta.page === 1
                        ? [...action.payload.prospectConversations]
                        : [...state.prospectConversations, ...action.payload.prospectConversations],
                metaProspectConversations: action.payload.meta,
                filter: action.payload.filter,
                query: action.payload.query
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATIONS_ERROR_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversationsError,
                loading: false,
                error: action.payload.error
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATION_START_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversationDetail,
                loading: true
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATION_SUCCESS_ACTION_TYPE:
            const isPagination = action.payload.page > 1;
            const newDetail = action.payload.prospectConversationDetail;

            let combinedMessages = newDetail.messages;
            if (isPagination && state.prospectConversationDetail) {
                const existingMessages = state.prospectConversationDetail.messages || [];

                const existingIds = new Set(existingMessages.map(m => m.id));
                const filteredNew = newDetail.messages.filter(m => !existingIds.has(m.id));

                combinedMessages = [...filteredNew, ...existingMessages];
            }

            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversationDetailSuccess,
                loading: false,
                prospectConversationDetail: {
                    ...newDetail,
                    messages: combinedMessages
                }
            };
        case PROSPECTMESSAGES_FETCH_CONVERSATION_ERROR_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.FetchingConversationDetailError,
                loading: false,
                error: action.payload.error
            };
        case PROSPECTMESSAGES_SAVE_START_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.Saving,
                loading: true
            };
        case PROSPECTMESSAGES_SAVE_SUCCESS_ACTION_TYPE:
            const messages = state.prospectConversationDetail?.messages
                ? [...state.prospectConversationDetail.messages, action.payload.message].sort(
                      (a, b) => moment(b.sentAt).valueOf() - moment(a.sentAt).valueOf()
                  )
                : [action.payload.message];
            const detail = {
                ...state.prospectConversationDetail,
                lastMessage: action.payload.message,
                messages: messages
            } as ProspectConversationDetail;
            return {
                ...state,
                prospectConversations: updatedConversations(action.payload.message, state),
                status: ProspectMessageStatus.SaveSuccess,
                prospectConversationDetail: detail,
                loading: false
            };
        case PROSPECTMESSAGES_SAVE_ERROR_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.Error,
                loading: false,
                saveMessageError: action.payload.error
            };
        case PROSPECTMESSAGES_PATCH_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.UpdateSuccess,
                loading: false
            };
        case SAVE_NEW_MESSAGE_START_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.SavingEmptyMessage,
                loading: true,
                error: null
            };
        case SAVE_NEW_MESSAGE_SUCCESS_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.SaveEmptyMessageSuccess,
                loading: false,
                newMessage: action.payload.message
            };
        case SAVE_NEW_MESSAGE_ERROR_ACTION_TYPE:
            return {
                ...state,
                status: ProspectMessageStatus.SaveEmptyMessageError,
                loading: false,
                newMessage: undefined,
                error: action.payload.error
            };
        default:
            return state;
    }
};
